/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import VueRouter from 'vue-router';
import { get as _get, mapValues, isArray, isEmpty, clamp, merge, pick } from 'lodash';
import { ActionTree, ActionContext } from 'vuex';

import { Audit, AuditStorage } from '@/pages/audit_setup_wizard/types';
import { DropdownOption } from '@/components/cards/types';
import { Service } from '@/pages/audit_setup_wizard/asw_service';
import { StorageFolder } from '@/pages/audit_setup_wizard/components/storage_integration/types';

import * as types from './mutationTypes';
import { prepareLoadedData, prepareSavingData } from './helpers';
import { State } from './state';

type ActionsDef = ReturnType<typeof createActions>;
type PayloadType<T> = T extends (ctx: any, payload: infer P) => any ? P : any;
export type Actions = { [P in keyof ActionsDef]: (payload: PayloadType<ActionsDef[P]>) => ReturnType<ActionsDef[P]> };

const createActions = (service: Service, router: VueRouter) => {
  class ActionsDef {
    // Error handling
    handleError({ dispatch }: ActionContext, error: Response) {
      const notifications = _get(error, 'response.data.errors.notifications', []);

      notifications.forEach(n => {
        dispatch(
          'alerts/show',
          {
            type: _get(n, 'type', 'error'),
            message: _get(n, 'message', 'Unknown error.'),
          },
          { root: true }
        );
      });

      if (!notifications.length) {
        dispatch(
          'alerts/show',
          {
            type: 'error',
            message: _get(error, 'response.data.message', 'Unknown error.'),
          },
          { root: true }
        );
      }
    }

    // Service actions
    async loadAudit({ dispatch, commit, getters }: ActionContext) {
      commit(types.SET_LOADING);
      try {
        const audit = prepareLoadedData(await service.load(getters.getAuditId));

        if (audit.managedIn.value === 'JIRA') {
          dispatch('setInitialJiraData', audit.jira);
          dispatch('setJiraTitle', audit.title);
        }

        if (audit.assessmentTemplate && audit.assessmentTemplate.assessors && audit.assessmentTemplate.verifiers) {
          dispatch('setPerformingAssessment', true);
        } else {
          audit.assessmentTemplate = {};
          dispatch('setPerformingAssessment', false);
        }

        if (audit.mappedRequests.length) {
          dispatch('setRequestMapper', true);
        }

        commit(types.SET_AUDIT, audit);
        commit(types.SET_AUDIT_ORIGINAL_STATE, audit);
        commit(types.SET_LOADING, false);
      } catch (error) {
        const errorMessage: string = _get(error, 'response.data.message', 'Unknown error.');

        if (errorMessage === 'Requested Audit does not exist.') {
          dispatch('resetAudit');
        }

        dispatch('handleError', error);
      }
    }
    async saveAndClose({ dispatch, getters }: ActionContext) {
      await dispatch('saveAudit');

      let redirectUrl = '';
      switch (getters.getAuditStatus) {
        case 'Draft': {
          redirectUrl = '/reports/audits/draft';
          break;
        }
        case 'Completed': {
          redirectUrl = '/reports/audits/completed';
          break;
        }
        default: {
          redirectUrl = '/reports/audits/active';
          break;
        }
      }
      await dispatch('redirect', redirectUrl, { root: true });
    }
    async saveAudit({ state, dispatch, getters, commit, rootGetters }: ActionContext): Promise<boolean> {
      commit(types.SET_SAVING);
      commit(types.SET_AUDIT_ERRORS, {});

      const auditData = prepareSavingData(getters.getAudit, state.jiraBus);
      try {
        const audit = prepareLoadedData(await service.save(auditData, rootGetters.csrfToken));
        commit(types.SET_AUDIT, audit);
      } catch (error) {
        const auditError: { [key: string]: string | string[] } = _get(error, ['response', 'data', 'errors', 'audit']);
        if (!isEmpty(auditError)) {
          // Backend sometimes returns inconsistent response. The error message can
          // be a string or an array of strings. That's why we need to sanitize
          // audit error object.
          const sanitizedAuditError = mapValues<typeof auditError, string[]>(auditError, value =>
            isArray(value) ? value : [value]
          );
          commit(types.SET_AUDIT_ERRORS, sanitizedAuditError);
        } else {
          dispatch('handleError', error);
        }

        commit(types.SET_SAVING, false);
        return false;
      }
      commit(types.SET_SAVING, false);

      return true;
    }
    async startAudit({ state, dispatch, getters, commit, rootGetters }: ActionContext) {
      if (getters.getAuditStatus !== 'Draft') {
        return;
      }

      commit(types.SET_SAVING);
      const auditData = prepareSavingData(getters.getAudit, state.jiraBus);

      try {
        const { redirectUrl } = await service.start(auditData, rootGetters.csrfToken);
        await dispatch('redirect', redirectUrl, { root: true });
      } catch (error) {
        dispatch('handleError', error);
      }
      commit(types.SET_SAVING, false);
    }
    async loadWizard({ dispatch, commit, getters }: ActionContext) {
      commit(types.SET_LOADING);
      try {
        const {
          currentUser,
          jira,
          flagNewTicketing,
          servicenow,
          templateAssessors,
          templateVerifiers,
        } = await service.loadInfo();
        commit(types.SET_JIRA_ENABLED, jira);
        commit(types.SET_SERVICENOW_ENABLED, servicenow);
        commit(types.SET_TEMPLATE_ASSESSORS, templateAssessors);
        commit(types.SET_TEMPLATE_VERIFIERS, templateVerifiers);
        commit(types.SET_FLAG_NEW_TICKETING, flagNewTicketing);

        // Fetch audit data
        if (getters.getAuditId !== -1) {
          dispatch('loadAudit');
        } else {
          commit(types.SET_AUDIT_MANAGERS, currentUser);
          commit(types.SET_LOADING, false);
        }

        dispatch('loadStorageIntegration');
      } catch (error) {
        dispatch('handleError', error);
      }
    }
    async loadStorageIntegration({ commit }: ActionContext) {
      const isListingFoldersSupported: boolean = await service.getIsListingFoldersSupported();

      commit(types.SET_STORAGE_INTEGRATION_ENABLED, isListingFoldersSupported);
    }

    async validateTitle({ dispatch, commit, getters, state }: ActionContext<State, any>) {
      commit(types.CLEAR_AUDIT_ERROR, 'title');
      const title = getters.getAudit.title;

      if (isEmpty(title)) {
        commit(types.SET_AUDIT_ERROR, { field: 'title', value: ['Title is required.'] });
        return;
      }

      const originalTitle = _get(state, ['auditOriginalState', 'title']);
      if (title === originalTitle) return;

      try {
        const valid = await service.validateTitle(title);
        if (!valid) {
          commit(types.SET_AUDIT_ERROR, {
            field: 'title',
            value: ['Audit title is already taken, please try another one.'],
          });
        }
      } catch (error) {
        dispatch('handleError', error);
      }
    }
    async validateStorageType({ dispatch, commit, getters }: ActionContext<State, any>) {
      commit(types.CLEAR_AUDIT_ERROR, 'type');

      const type = getters.getAudit.managedIn.value;
      try {
        const valid = await service.validateStorageType(type);
        if (!valid) {
          commit(types.SET_AUDIT_ERROR, {
            field: 'managedIn',
            value: ['This storage type is not configured. ' + 'Please configure it in settings first.'],
          });
        } else if (getters.getAuditError('managedIn') != null) {
          commit(types.SET_AUDIT_ERROR, {
            field: 'managedIn',
            value: [],
          });
        }
      } catch (error) {
        dispatch('handleError', error);
      }
    }

    // UI actions
    async setStep({ dispatch, getters }: ActionContext<State, any>, stepToSet: number) {
      const currentStep = getters.currentStep;

      if (currentStep === 1) {
        const isSaveOk = <boolean>await dispatch('saveAudit');
        if (!isSaveOk) return;
      } else if (currentStep === 4) {
        await dispatch('saveAssessmentTemplate');
      }

      router.push({
        name: 'auditSetupWizard/edit',
        params: {
          auditId: getters.getAuditId,
          step: clamp(stepToSet, 1, getters.getStepInfo.length).toString(),
        },
      });

      await dispatch('loadAudit');
    }
    goToPreviousStep({ dispatch, getters }: ActionContext) {
      if (getters.hasPreviousStep) {
        dispatch('setStep', getters.currentStep - 1);
      }
    }
    goToNextStep({ dispatch, getters }: ActionContext) {
      if (getters.hasNextStep) {
        dispatch('setStep', getters.currentStep + 1);
      }
    }
    setRequestMapper({ commit }: ActionContext, show: boolean) {
      commit(types.SET_REQUEST_MAPPER, show);
    }
    toggleRequestMapper({ commit, getters }: ActionContext) {
      commit(types.SET_REQUEST_MAPPER, !getters.isRequestMapperVisible);
    }

    // Audit mutation actions
    resetAudit({ commit }: ActionContext) {
      router.replace({ name: 'auditSetupWizard/new' });

      commit(types.RESET_AUDIT);
      commit(types.SET_LOADING, false);
    }
    setAudit({ commit, getters }: ActionContext, audit: Audit) {
      // Use merge to handle partial responses
      commit(types.SET_AUDIT, merge(getters.getAudit, audit));
    }
    setAuditTitle({ commit, dispatch }: ActionContext, title: string) {
      commit(types.SET_AUDIT_TITLE, title);
      commit(types.SET_JIRA_TITLE, title);
      dispatch('validateTitle');
    }
    setAuditManagers({ commit }: ActionContext, managers: DropdownOption[]) {
      commit(types.SET_AUDIT_MANAGERS, managers);
    }
    setAuditRelatedProgram({ commit }: ActionContext, program: string) {
      commit(types.SET_AUDIT_RELATED_PROGRAM, program);
    }
    setAuditStartDate({ commit }: ActionContext, date: string) {
      commit(types.SET_AUDIT_START_DATE, date);
    }
    setAuditEndDate({ commit }: ActionContext, date: string) {
      commit(types.SET_AUDIT_END_DATE, date);
    }
    setAuditReportStartDate({ commit }: ActionContext, date: string) {
      commit(types.SET_AUDIT_REPORT_START_DATE, date);
    }
    setAuditReportEndDate({ commit }: ActionContext, date: string) {
      commit(types.SET_AUDIT_REPORT_END_DATE, date);
    }
    setAuditType({ commit }: ActionContext, type: string) {
      commit(types.SET_AUDIT_TYPE, type);
    }
    setAuditFirm({ commit }: ActionContext, firm: string) {
      commit(types.SET_AUDIT_FIRM, firm);
    }
    setAuditors({ commit }: ActionContext, auditors: DropdownOption[]) {
      commit(types.SET_AUDIT_AUDITORS, auditors);
    }
    setAuditStorageType({ commit, dispatch }: ActionContext, storage: string) {
      commit(types.SET_AUDIT_STORAGE, storage);
      dispatch('validateStorageType');
    }

    // JIRA
    setJiraData({ commit }: ActionContext, jiraData: any) {
      commit(types.SET_JIRA_DATA, jiraData);
    }
    setInitialJiraData({ commit }: ActionContext, jiraData: any) {
      commit(types.SET_INITIAL_JIRA_DATA, jiraData);
    }
    setJiraTitle({ commit }: ActionContext, title: string) {
      commit(types.SET_JIRA_TITLE, title);
    }

    setSyncExternalComments({ commit }: ActionContext, value: boolean) {
      commit(types.SET_SYNC_EXTERNAL_COMMENTS, value);
    }
    setSyncExternalAttachments({ commit }: ActionContext, value: boolean) {
      commit(types.SET_SYNC_EXTERNAL_ATTACHMENTS, value);
    }

    loadJiraProjects() {
      return service.loadJiraProjects();
    }
    loadJiraProject(_: ActionContext, projectId: number) {
      return service.loadJiraProject(projectId);
    }
    loadJiraIssueLinkTypes() {
      return service.loadJiraIssueLinkTypes();
    }

    // Remote Requests (Jira, ServiceNow)
    async loadRemoteRequests({ dispatch, getters }: ActionContext) {
      let data = [];
      try {
        data = await service.loadRemoteRequests(getters.getAuditId, getters.getManagedIn);
      } catch (error) {
        dispatch('handleError', error);
      } finally {
        dispatch('setRemoteRequests', data);
      }
      return data;
    }

    setRemoteRequests({ commit }: ActionContext, requests: object[]) {
      commit(types.SET_REMOTE_REQUESTS, requests);
    }

    // Assessment template
    setPerformingAssessment({ commit }: ActionContext, performingAssessment: boolean) {
      commit(types.SET_PERFORMING_ASSESSMENT, performingAssessment);
    }
    setAssessors({ commit }: ActionContext, assessors: DropdownOption) {
      commit(types.SET_ASSESSORS, assessors);
    }
    setVerifiers({ commit }: ActionContext, verifiers: DropdownOption) {
      commit(types.SET_VERIFIERS, verifiers);
    }
    async saveAssessmentTemplate({ commit, dispatch, getters, rootGetters }: ActionContext) {
      commit(types.SET_SAVING);
      let template = null;
      if (getters.performingAssessment) {
        template = {
          assessors: getters.getAudit.assessmentTemplate.assessors.value,
          verifiers: getters.getAudit.assessmentTemplate.verifiers.value,
        };
      }
      try {
        await service.saveAssessmentTemplate(template, getters.getAuditId, rootGetters.csrfToken);
      } catch (error) {
        dispatch('handleError', error);
      }
      commit(types.SET_SAVING, false);
    }

    // Storage integration
    loadIsListingFoldersSupported() {
      return service.getIsListingFoldersSupported();
    }
    loadFolder(_: ActionContext, auditId: number) {
      return service.getFolder(auditId);
    }
    loadListFolders(_: ActionContext, storageFolder: StorageFolder) {
      return service.getListFolders(storageFolder);
    }
    setStorageIntegrationData({ commit }: ActionContext, storageFolder: StorageFolder | null) {
      const auditStorage: AuditStorage | null = pick(storageFolder, ['location', 'integrationId'])
        ? storageFolder
        : null;

      commit(types.SET_STORAGE_INTEGRATION_DATA, auditStorage);
    }
  }

  return ActionsDef.prototype;
};

export default (createActions as any) as (service: Service, router: VueRouter) => ActionTree<State, any>;
